<template>
  <Layout>
    <McLoading :isLoading="isTransitionLoading" />
    <div class="mc-fleet-task">
      <div :class="['mc-list-header', { 'mc-list-header--sticky': isStickyHeader }]">
        <PageHeader
          class="page-header"
          :title="title"
          :sub-title="subTitle"
          :isSticky="isStickyHeader"
        >
          <template v-if="!isMobileHeader" #afterTitle>
            <div class="d-flex gap-2 fleet-task-header-actions">
              <McListDownloader
                v-if="showDownloadButton"
                class="fleet-task-header-actions__download"
                :pageOptions="downloadPageOptions"
                :fileFormatOptions="['excel']"
                :buttonProps="downloadButtonProps"
                @download="handleDownload"
              />
              <McButton
                class="fleet-task-header-actions__upload"
                variant="secondary"
                :text="$t('global.action.upload')"
                icon="icon-outline_upload"
                :screenBreakpoint="buttonScreenBreakpoint"
                tooltipPosition="bottom"
                @click="handleImport"
              />
              <McButton
                v-if="showCreateButton"
                class="fleet-task-header-actions__create-button"
                variant="primary"
                :text="$t('fleetTask.scheduleDelivery')"
                @click="handleAdd"
              />
              <b-dropdown
                v-if="showCreateDropdown"
                class="create-dropdown fleet-task-header-actions__create-dropdown"
                toggle-tag="div"
                toggle-class="p-0 bg-transparent"
              >
                <template #button-content>
                  <McButton
                    variant="primary"
                    icon="icon-outline_chevron-down"
                    icon-position="right"
                    :text="$t('fleetTask.action.scheduleFleetPlanning')"
                  />
                </template>
                <b-dropdown-item
                  v-for="item in dropdownItems"
                  :key="item.key"
                  class="create-dropdown-item"
                  @click="$router.push({ name: item.routeName })"
                >
                  {{ item.label }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
          <template v-if="isMobileHeader" #footer>
            <div class="d-flex gap-2 fleet-task-header-actions fleet-task-header-actions--mobile">
              <McListDownloader
                v-if="showDownloadButton"
                class="fleet-task-header-actions__download"
                :pageOptions="downloadPageOptions"
                :fileFormatOptions="['excel']"
                :buttonProps="downloadButtonProps"
                @download="handleDownload"
              />
              <McButton
                class="fleet-task-header-actions__upload"
                variant="secondary"
                :text="$t('global.action.upload')"
                icon="icon-outline_upload"
                :screenBreakpoint="buttonScreenBreakpoint"
                tooltipPosition="bottom"
                @click="handleImport"
              />
              <McButton
                v-if="showCreateButton"
                class="fleet-task-header-actions__create-button"
                variant="primary"
                :text="$t('fleetTask.scheduleDelivery')"
                @click="handleAdd"
              />
              <b-dropdown
                v-if="showCreateDropdown"
                class="create-dropdown fleet-task-header-actions__create-dropdown"
                toggle-tag="div"
                toggle-class="p-0 bg-transparent"
              >
                <template #button-content>
                  <McButton
                    variant="primary"
                    icon="icon-outline_chevron-down"
                    icon-position="right"
                    :text="$t('fleetTask.action.scheduleFleetPlanning')"
                  />
                </template>
                <b-dropdown-item
                  v-for="item in dropdownItems"
                  :key="item.key"
                  class="create-dropdown-item"
                  @click="$router.push({ name: item.routeName })"
                >
                  {{ item.label }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </PageHeader>
      </div>

      <b-card class="mc-card__setting">
        <template v-if="hasAccessFleetTask && hasAccessFleetTaskInstant && !isTmsMarketing">
          <b-tabs lazy content-class="p-4">
            <b-tab
              :active="selectedTab === 'withLoad'"
              :title-item-class="!hasAccessFleetTask ? 'd-none' : ''"
              @click="changeTab('withLoad')"
            >
              <template #title>
                <span>{{ $t('fleetTask.tab.withLoad') }}</span>
              </template>
              <McTable
                v-show="isTableDataAvailable || (!isTableDataAvailable && hasFilters)"
                ref="mcTableWithLoad"
                searchable
                stickyHeader
                disableStickyOnMobile
                bulkDeleteApi
                hasEmit:deleteError
                hasCheckbox
                hasDelete
                :hasSelectAll="enableSelectAll"
                :fields="fields"
                :apiClient="apiClient"
                :activeFilters="activeFilters"
                :customHandlers="customHandlers"
                :initialPage="initialParams.page"
                :initialLimit="initialParams.limit"
                :initialSearch="initialParams.search"
                @deleteError="handleDeleteError"
                @available-change="handleDataAvailable"
                @handleSelectValue="handleSelectValue"
              >
                <template #before-table="{ isSelected, showDeleteModal }">
                  <div class="fleet-task-table-toolbar mb-3">
                    <div class="d-flex justify-content-between fleet-task-table-toolbar__row">
                      <McInlineFilter
                        class="fleet-task-table-toolbar__inline-filter"
                        :filterItems="inlineFilterItems"
                        :activeKeys="activeFilters['status.name']"
                        :hasNavigation="isMobileHeader"
                        @update:activeKeys="
                          handleChangeActiveFilters([{ key: 'status.name', values: $event }])
                        "
                      />

                      <div
                        v-if="!isMobileHeader"
                        class="d-flex gap-2 fleet-task-table-toolbar__actions"
                      >
                        <McTableFilter
                          :activeFilters="activeFilters"
                          :parseFilterDataMappings="parseFilterDataMappings"
                          @submit="handleChangeActiveFilters"
                        >
                          <template
                            #content="{
                              formValues,
                              updateFormValues,
                              fullObjects,
                              updateFullObjects,
                            }"
                          >
                            <InputTableFilter
                              :formValues="formValues"
                              :fullObjects="fullObjects"
                              @update:formValues="updateFormValues($event)"
                              @update:fullObjects="updateFullObjects($event)"
                            />
                          </template>
                        </McTableFilter>

                        <McActionsDropdown
                          v-if="hasBulkTransitionFleetTaskFlag"
                          :disabled="!isSelected"
                          :menuItems="actionMenuItems(showDeleteModal)"
                        />

                        <McButton
                          v-if="isSelected && !hasBulkTransitionFleetTaskFlag"
                          variant="secondary-red"
                          icon="icon-outline_trash"
                          :text="$t('global.delete')"
                          @click="showDeleteModal"
                        />
                      </div>
                    </div>
                  </div>
                </template>

                <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
                  <McFilterSelect
                    class="fleet-task-table-toolbar__view-manager"
                    :defaultColumnOrder="initialFields"
                    :currentColumnOrder="dataFields"
                    :buttonText="isMobileHeader ? '' : undefined"
                    :buttonProps="isMobileHeader ? mobileTableToolbarButtonProps : {}"
                    @setColumn="handleSetFleetTaskColumns($event, handleSetDataFields)"
                  />
                </template>
                <template v-if="isMobileHeader" #afterSearchBar>
                  <div class="d-flex gap-2 fleet-task-table-toolbar__search-actions">
                    <McTableFilter
                      :activeFilters="activeFilters"
                      :parseFilterDataMappings="parseFilterDataMappings"
                      :buttonProps="mobileTableToolbarButtonProps"
                      @submit="handleChangeActiveFilters"
                    >
                      <template
                        #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }"
                      >
                        <InputTableFilter
                          :formValues="formValues"
                          :fullObjects="fullObjects"
                          @update:formValues="updateFormValues($event)"
                          @update:fullObjects="updateFullObjects($event)"
                        />
                      </template>
                    </McTableFilter>

                    <McActionsDropdown
                      v-if="hasBulkTransitionFleetTaskFlag"
                      :disabled="selectedFleetTaskCount === 0"
                      :menuItems="mobileActionMenuItems"
                      :buttonText="''"
                      :buttonProps="mobileActionButtonProps"
                    />

                    <McButton
                      v-else-if="selectedFleetTaskCount > 0"
                      variant="secondary-red"
                      icon="icon-outline_trash"
                      :text="$t('global.delete')"
                      @click="showActiveTableDeleteModal"
                    />
                  </div>
                  <h6
                    v-if="selectedFleetTaskCount"
                    class="mc-row-check mt-0 fleet-task-table-toolbar__selected-counter"
                  >
                    <span class="badge btn-success">{{ selectedFleetTaskCount }}</span>
                    {{ $t('global.object.selectedRow') }}
                    <a class="color-primary" @click="handleUnselectAllFleetTasks">{{
                      $t('global.action.deselect')
                    }}</a>
                  </h6>
                </template>

                <!-- status -->
                <template #[`cell(status.name)`]="{ item }">
                  <span
                    v-if="item.status?.color"
                    class="__status-dots"
                    :style="{ backgroundColor: item.status.color }"
                  ></span>
                  <span class="mc-text-status">{{ computedStatus(item) }}</span>
                </template>

                <template #cell(line_order)="{ value }">
                  {{ value === null || value === undefined || value === '' ? '-' : value }}
                </template>

                <!-- expected departure on -->
                <template #cell(expected_departure_on)="{ value }">
                  <template v-if="value">
                    <div>{{ formatIndexDate(value).date }}</div>
                    <div class="mc-datetime">
                      {{ formatIndexDate(value).time }}
                    </div>
                  </template>
                  <template v-else> - </template>
                </template>

                <!-- start_address -->
                <template #[`cell(start_address.name)`]="{ value }">
                  {{ !!value ? value : '-' }}
                </template>

                <!-- created on -->
                <template #cell(created_on)="{ value }">
                  <div>{{ formatIndexDate(value).date }}</div>
                  <div class="mc-datetime">
                    {{ formatIndexDate(value).time }}
                  </div>
                </template>

                <template #cell(origin_addresses)="{ value }">
                  <McChipList
                    v-if="value?.length"
                    :items="value"
                    :max-visible="3"
                    itemTextField="address_name"
                    showAllMode="dropdown"
                    :showAllDropdownTitle="$t('global.field.originAddress')"
                    showAllDropdownTrigger="action"
                  />
                </template>

                <template #cell(destination_addresses)="{ value }">
                  <McChipList
                    v-if="value?.length"
                    :items="value"
                    :max-visible="3"
                    itemTextField="address_name"
                    showAllMode="dropdown"
                    :showAllDropdownTitle="$t('global.field.destinationAddress')"
                    showAllDropdownTrigger="action"
                  />
                </template>

                <!-- vehicle.license_plate -->
                <template #[`cell(vehicle.license_plate)`]="{ item }">
                  {{ item?.vehicle?.license_plate ?? '-' }}
                  <div v-if="item?.vehicle?.hull_no" class="text-muted">
                    {{ item?.vehicle?.hull_no }}
                  </div>
                </template>

                <!-- driver_user -->
                <template #[`cell(driver_user.name)`]="{ value }">
                  {{ !!value ? value : '-' }}
                </template>

                <!-- load_weight -->
                <template #cell(load_weight)="{ value }"> {{ value }} Kg </template>

                <!-- load_volume -->
                <template #cell(load_volume)="{ value }"> {{ value }} Cbm </template>
                <!-- Track FO -->
                <template #cell(track_fo)="data">
                  <McButton
                    variant="secondary"
                    :text="$t('global.action.share.text')"
                    icon="icon-fill_share"
                    size="sm"
                    :disabled="isDisabled(data.item)"
                    :displayTooltip="true"
                    :tooltipText="showTooltip(data.item)"
                    tooltipPosition="left"
                    @click="trackFO(data.item)"
                  />
                </template>
              </McTable>
              <b-card v-if="!isTableDataAvailable && !hasFilters" class="mt-3">
                <div class="empty-table-container">
                  <McEmptyState
                    :title="$t('fleetTask.emptyState.title')"
                    :desc="$t('fleetTask.emptyState.description')"
                    class="pb-4"
                    placementType="list"
                    :hasButton="true"
                    size="lg"
                  />
                </div>
              </b-card>
            </b-tab>
            <b-tab
              :active="selectedTab === 'withoutLoad'"
              :title-item-class="!hasAccessFleetTaskInstant ? 'd-none' : ''"
              @click="changeTab('withoutLoad')"
            >
              <template v-if="hasAccessFleetTaskInstant && hasAccessFleetTask" #title>
                <span>{{ $t('fleetTask.tab.withoutLoad') }}</span>
              </template>
              <FleetTaskInstant :withCard="!hasAccessFleetTask" />
            </b-tab>
          </b-tabs>
        </template>

        <template v-else>
          <template v-if="hasAccessFleetTask">
            <McTable
              v-show="isTableDataAvailable || (!isTableDataAvailable && hasFilters)"
              ref="mcTableWithoutLoad"
              searchable
              stickyHeader
              disableStickyOnMobile
              bulkDeleteApi
              hasEmit:deleteError
              hasCheckbox
              hasDelete
              :withCard="true"
              :hasSelectAll="enableSelectAll"
              :fields="fields"
              :apiClient="apiClient"
              :activeFilters="activeFilters"
              :customHandlers="customHandlers"
              :initialPage="initialParams.page"
              :initialLimit="initialParams.limit"
              :initialSearch="initialParams.search"
              @deleteError="handleDeleteError"
              @available-change="handleDataAvailable"
              @handleSelectValue="handleSelectValue"
            >
              <template #before-table="{ isSelected, showDeleteModal }">
                <div class="fleet-task-table-toolbar mb-3">
                  <div class="d-flex justify-content-between fleet-task-table-toolbar__row">
                    <McInlineFilter
                      class="fleet-task-table-toolbar__inline-filter"
                      :filterItems="inlineFilterItems"
                      :activeKeys="activeFilters['status.name']"
                      :hasNavigation="isMobileHeader"
                      @update:activeKeys="
                        handleChangeActiveFilters([{ key: 'status.name', values: $event }])
                      "
                    />

                    <div
                      v-if="!isMobileHeader"
                      class="d-flex gap-2 fleet-task-table-toolbar__actions"
                    >
                      <McTableFilter
                        :activeFilters="activeFilters"
                        :parseFilterDataMappings="parseFilterDataMappings"
                        @submit="handleChangeActiveFilters"
                      >
                        <template
                          #content="{
                            formValues,
                            updateFormValues,
                            fullObjects,
                            updateFullObjects,
                          }"
                        >
                          <InputTableFilter
                            :formValues="formValues"
                            :fullObjects="fullObjects"
                            @update:formValues="updateFormValues($event)"
                            @update:fullObjects="updateFullObjects($event)"
                          />
                        </template>
                      </McTableFilter>

                      <McActionsDropdown
                        v-if="hasBulkTransitionFleetTaskFlag"
                        :disabled="!isSelected"
                        :menuItems="actionMenuItems(showDeleteModal)"
                      />

                      <McButton
                        v-if="isSelected && !hasBulkTransitionFleetTaskFlag"
                        variant="secondary-red"
                        :text="$t('global.delete')"
                        icon="icon-outline_trash"
                        @click="showDeleteModal"
                      />
                    </div>
                  </div>
                </div>
              </template>
              <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
                <McFilterSelect
                  class="fleet-task-table-toolbar__view-manager"
                  :defaultColumnOrder="initialFields"
                  :currentColumnOrder="dataFields"
                  :buttonText="isMobileHeader ? '' : undefined"
                  :buttonProps="isMobileHeader ? mobileTableToolbarButtonProps : {}"
                  @setColumn="handleSetFleetTaskColumns($event, handleSetDataFields)"
                />
              </template>
              <template v-if="isMobileHeader" #afterSearchBar>
                <div class="d-flex gap-2 fleet-task-table-toolbar__search-actions">
                  <McTableFilter
                    :activeFilters="activeFilters"
                    :parseFilterDataMappings="parseFilterDataMappings"
                    :buttonProps="mobileTableToolbarButtonProps"
                    @submit="handleChangeActiveFilters"
                  >
                    <template
                      #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }"
                    >
                      <InputTableFilter
                        :formValues="formValues"
                        :fullObjects="fullObjects"
                        @update:formValues="updateFormValues($event)"
                        @update:fullObjects="updateFullObjects($event)"
                      />
                    </template>
                  </McTableFilter>

                  <McActionsDropdown
                    v-if="hasBulkTransitionFleetTaskFlag"
                    :disabled="selectedFleetTaskCount === 0"
                    :menuItems="mobileActionMenuItems"
                    :buttonText="''"
                    :buttonProps="mobileActionButtonProps"
                  />

                  <McButton
                    v-else-if="selectedFleetTaskCount > 0"
                    variant="secondary-red"
                    :text="$t('global.delete')"
                    icon="icon-outline_trash"
                    @click="showActiveTableDeleteModal"
                  />
                </div>
                <h6
                  v-if="selectedFleetTaskCount"
                  class="mc-row-check mt-0 fleet-task-table-toolbar__selected-counter"
                >
                  <span class="badge btn-success">{{ selectedFleetTaskCount }}</span>
                  {{ $t('global.object.selectedRow') }}
                  <a class="color-primary" @click="handleUnselectAllFleetTasks">{{
                    $t('global.action.deselect')
                  }}</a>
                </h6>
              </template>
              <!-- status -->
              <template #[`cell(status.name)`]="{ item }">
                <span
                  v-if="item.status?.color"
                  class="__status-dots"
                  :style="{ backgroundColor: item.status.color }"
                ></span>
                <span class="mc-text-status">{{ computedStatus(item) }}</span>
              </template>

              <template #cell(line_order)="{ value }">
                {{ value === null || value === undefined || value === '' ? '-' : value }}
              </template>

              <!-- expected departure on -->
              <template #cell(expected_departure_on)="{ value }">
                <template v-if="value">
                  <div>{{ formatIndexDate(value).date }}</div>
                  <div class="mc-datetime">
                    {{ formatIndexDate(value).time }}
                  </div>
                </template>
                <template v-else> - </template>
              </template>

              <!-- start_address -->
              <template #[`cell(start_address.name)`]="{ value }">
                {{ !!value ? value : '-' }}
              </template>

              <!-- created on -->
              <template #cell(created_on)="{ value }">
                <div>{{ formatIndexDate(value).date }}</div>
                <div class="mc-datetime">
                  {{ formatIndexDate(value).time }}
                </div>
              </template>

              <template #cell(origin_addresses)="{ value }">
                <McChipList
                  v-if="value?.length"
                  :items="value"
                  :max-visible="3"
                  itemTextField="address_name"
                  showAllMode="dropdown"
                  :showAllDropdownTitle="$t('global.field.originAddress')"
                  showAllDropdownTrigger="action"
                />
              </template>

              <template #cell(destination_addresses)="{ value }">
                <McChipList
                  v-if="value?.length"
                  :items="value"
                  :max-visible="3"
                  itemTextField="address_name"
                  showAllMode="dropdown"
                  :showAllDropdownTitle="$t('global.field.destinationAddress')"
                  showAllDropdownTrigger="action"
                />
              </template>

              <!-- vehicle.license_plate -->
              <template #[`cell(vehicle.license_plate)`]="{ value }">
                {{ !!value ? value : '-' }}
              </template>

              <!-- driver_user -->
              <template #[`cell(driver_user.name)`]="{ value }">
                {{ !!value ? value : '-' }}
              </template>

              <!-- load_weight -->
              <template #cell(load_weight)="{ value }"> {{ value }} Kg </template>

              <!-- load_volume -->
              <template #cell(load_volume)="{ value }"> {{ value }} Cbm </template>
              <!-- Track FO -->
              <template #cell(track_fo)="data">
                <McButton
                  variant="secondary"
                  :text="$t('global.action.share.text')"
                  icon="icon-fill_share"
                  size="sm"
                  :disabled="isDisabled(data.item)"
                  :displayTooltip="true"
                  :tooltipText="showTooltip(data.item)"
                  tooltipPosition="left"
                  @click="trackFO(data.item)"
                />
              </template>
            </McTable>

            <b-card v-if="!isTableDataAvailable && !hasFilters" class="mt-3">
              <div class="empty-table-container">
                <McEmptyState
                  :title="$t('fleetTask.emptyState.title')"
                  :desc="$t('fleetTask.emptyState.description')"
                  class="pb-4"
                  placementType="list"
                  :hasButton="true"
                  size="lg"
                />
              </div>
            </b-card>
          </template>
          <template v-else-if="hasAccessFleetTaskInstant">
            <FleetTaskInstant :withCard="!hasAccessFleetTask" />
          </template>
        </template>
      </b-card>
      <ModalShare :detailData="dataFO" />
      <DefaultTransitionModal
        ref="bulkTransitionModal"
        :entityData="{
          number: getSelectedItemsCountText(selectedItemsForTransition.length),
          status: { name: $t(oldStatusFleetTaskLabel) },
        }"
        :newStatusTransition="newStatusTransition"
        @transition="handleBulkTransitionConfirm"
        @cancel="handleCancelBulkTransition"
      >
        <template #confirmationText>
          <p
            v-html="
              $t('fleetTask.bulkTransition.confirmSendSelected', {
                count: hasLimitBulkTransition
                  ? `${batchSizeBulkTransition} / ${selectedItemsForTransition.length}`
                  : selectedItemsForTransition.length,
              })
            "
          />
        </template>
      </DefaultTransitionModal>
      <TransitionToCancelModal
        ref="transitionToCancelModal"
        :entityData="{
          number: getSelectedItemsCountText(selectedItemsForTransition.length),
          status: fleetTaskStatuses.DRAFT,
        }"
        :newStatusTransition="newStatusTransition"
        @transition="handleBulkTransitionConfirm"
      />
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'

import PageHeader from '@/components/page-header-v2'
import McTable from '@/components/McTable'
import McButton from '@/components/McButton'
import McInlineFilter from '@/components/McInlineFilter'
import McListDownloader from '@/components/McListDownloader'
import McTableFilter from '@/components/McTableFilter'
import McFilterSelect from '@/components/McFilterSelect'
import InputTableFilter from './components/InputTableFilter'
import McChipList from '@/components/McChipList'
import McActionsDropdown from '@/components/McActionsDropdown'
import DefaultTransitionModal from '@/components/McTransitionStatus/DefaultTransitionModal'
import ModalShare from './components/Modal/Share'
import TransitionToCancelModal from './components/TransitionToCancelModal'

import * as FleetTask from '@/services/rest-api/lib/fleet-task/fleet-task'
import * as Transition from '@/services/rest-api/lib/fleet-task/transition'

import inlineFilterItems from './lib/constants/inline-filter-items'
import { fleetTaskListFields } from './lib/fields/fleetTaskDetailFields'
import filterMappings from './lib/constants/filter-mappings'
import toastErrors from './lib/constants/toast-errors'
import parseFilterDataMappings from './lib/constants/parse-filter-data-mappings'
import formatFilterMappings from './lib/constants/format-filter-mappings'
import { statusIdToLabelMap } from './lib/constants/status'

import FleetTaskInstant from '../fleet-task-instant'

import moment from 'moment'
import { formatIndexDate } from '@/helpers/tzDate'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

import isEqual from 'lodash/isEqual'
import omitBy from 'lodash/omitBy'
import McEmptyState from '@/components/McEmptyState'
import status from './lib/constants/status'

import { isTmsMarketingUser } from '@/helpers/tms-access'

moment.locale('id')

export default {
  page: {
    title: 'Fleet Task',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McTable,
    McButton,
    McInlineFilter,
    McListDownloader,
    McTableFilter,
    McFilterSelect,
    InputTableFilter,
    ModalShare,
    FleetTaskInstant,
    McEmptyState,
    McChipList,
    McActionsDropdown,
    DefaultTransitionModal,
    TransitionToCancelModal,
  },
  mixins: [Helper],
  data() {
    return {
      activeFilters: {},
      params: {},
      dataFO: {},
      initialParams: {},
      isTableDataAvailable: true,
      metadata: null,
      selectedTab: 'withLoad',
      hasFilters: false,
      fleetTaskStatuses: status,
      statusIdToLabelMap,
      selectedFleetTaskIds: {},
      selectedFleetTasks: {},
      newStatusTransition: null,
      oldStatusFleetTaskLabel: status.DRAFT.label,
      selectedItemsForTransition: [],
      hasLimitBulkTransition: false,
      isTransitionLoading: false,
      isMobileHeader: false,
      hasPageScrolled: false,
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    hasBulkTransitionFleetTaskFlag() {
      const flag = this.getFeatureFlagValue('bulkTransitionFleetTaskFlagV2')
      const byCompanyId = flag?.company_id?.includes(this.getCompanyId()) ?? false

      const isAug5 = flag?.release_aug_5_2026
      const isAug19 = flag?.release_aug_19_2026

      return !!(isAug19 || (isAug5 && byCompanyId))
    },
    actionMenuItems() {
      return (showDeleteModal) => {
        if (this.isTmsMarketing) {
          return [
            {
              label: this.$t('global.delete'),
              class: 'text-red',
              handler: showDeleteModal,
            },
          ]
        }
        const flag = this.getFeatureFlagValue('bulkTransitionFleetTaskFlagV2')

        const isAug5 = flag?.release_aug_5_2026
        const isAug19 = flag?.release_aug_19_2026
        const statusChildren = []

        if (this.hasFeatureFleetTaskContractMGM) {
          statusChildren.push({
            label: this.$t('fleetTask.status.sendForApproval'),
            handler: this.handleSendApproval,
          })
        }

        if (isAug5 || isAug19) {
          statusChildren.push({
            label: this.$t('fleetTask.bulkTransition.action.scheduled'),
            handler: this.handleTransitionToScheduled,
          })
        }

        if (isAug19) {
          statusChildren.push(
            {
              label: this.$t('fleetTask.bulkTransition.action.started'),
              handler: this.handleTransitionStarted,
            },
            {
              label: this.$t('fleetTask.bulkTransition.action.ended'),
              handler: this.handleTransitionToEnded,
            },
            {
              label: this.$t('fleetTask.bulkTransition.action.canceled'),
              class: 'text-red',
              handler: this.handleTransitionToCanceled,
            }
          )
        }

        return [
          {
            label: this.$t('global.action.changeStatus'),
            children: statusChildren,
          },
          {
            label: this.$t('global.delete'),
            class: 'text-red',
            handler: showDeleteModal,
          },
        ]
      }
    },
    title() {
      if (!this.hasAccessFleetTask) {
        return this.$t('fleetTaskInstant.title.list')
      }

      return this.selectedTab === 'withoutLoad'
        ? this.$t('fleetTaskInstant.title.list')
        : this.$t('fleetTask.title.fleetPlanning')
    },
    subTitle() {
      if (!this.hasAccessFleetTask) {
        return this.$t('fleetTaskInstant.subtitle.list')
      }

      return this.selectedTab === 'withoutLoad'
        ? this.$t('fleetTaskInstant.subtitle.list')
        : this.$t('fleetTask.subtitle.fleetPlanning')
    },
    buttonScreenBreakpoint() {
      return 768
    },
    downloadButtonProps() {
      return {
        screenBreakpoint: this.buttonScreenBreakpoint,
        tooltipPosition: 'bottom',
      }
    },
    mobileTableToolbarButtonProps() {
      return {
        screenBreakpoint: 992,
        tooltipPosition: 'bottom',
      }
    },
    mobileActionButtonProps() {
      return {
        tooltipPosition: 'bottom',
      }
    },
    isStickyHeader() {
      return this.isMobileHeader && this.hasPageScrolled
    },
    showDownloadButton() {
      return this.hasAccessFleetTask && this.selectedTab === 'withLoad'
    },
    showCreateButton() {
      return !(this.hasAccessFleetTask && this.hasAccessFleetTaskInstant) || this.isTmsMarketing
    },
    showCreateDropdown() {
      return this.hasAccessFleetTask && this.hasAccessFleetTaskInstant && !this.isTmsMarketing
    },
    dropdownItems() {
      return [
        {
          key: 'fleet-task-create',
          routeName: 'Fleet Task Create',
          label: this.$t('fleetTask.action.create'),
        },
        {
          key: 'fleet-task-instant-create',
          routeName: 'Fleet Task Instant Create',
          label: this.$t('fleetTaskInstant.action.create'),
        },
      ]
    },
    fields() {
      const fields = []

      fleetTaskListFields(this).forEach((field) => {
        if (field.isShow === false) return
        if (field.scopes) {
          const hasAccess = this.hasAccessAll(field.scopes)
          if (!hasAccess) return
        }
        fields.push(field)
      })

      return this.normalizeLineOrderPosition(fields)
    },
    inlineFilterItems() {
      const status = inlineFilterItems(this)
      const statusMgm = ['WAITING_FOR_APPROVAL', 'APPROVED']

      return status.filter(
        (item) => this.hasFeatureFleetTaskContractMGM || !statusMgm.includes(item.id)
      )
    },
    apiClient() {
      let getFleetTask = FleetTask.get(null, this.handleSetParams, this.handleSetMetadata)

      if (Object.keys(this.activeFilters).length > 0) {
        const queryParamFilters = []

        // mapping the activeFilters to be sent as query params
        Object.keys(this.activeFilters).forEach((key) => {
          const filter = filterMappings[key]?.(key, this.activeFilters[key]) ?? undefined
          queryParamFilters.push(filter)
        })
        getFleetTask = FleetTask.get(
          queryParamFilters,
          this.handleSetParams,
          this.handleSetMetadata
        )
      }

      return {
        get: getFleetTask,
        delete: FleetTask.deleteMany,
      }
    },

    customHandlers() {
      return {
        handleClickRow: this.handleClickRow,
        handleOpenNewTab: this.handleOpenNewTab,
      }
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
    downloadPageOptions() {
      return [
        { label: this.$t('global.currentPage'), amount: this.metadata?.count, key: 'current_page' },
        { label: this.$t('global.allPages'), amount: this.metadata?.total_count, key: 'all' },
      ]
    },
    ...mapGetters({
      isWhitelabel: 'identity/isWhitelabel',
      isMetrack: 'identity/isMetrack',
      hasAccessAll: 'identity/hasAccessAll',
    }),

    illustrationPath() {
      if (this.isWhitelabel() || this.isMetrack()) {
        return require(`@/assets/images/illustration/whitelabel/no-data.svg`)
      }
      return require(`@/assets/images/illustration/saas/no-data.svg`)
    },
    hasAccessFleetTask() {
      return this.hasAccessAll(['tms:read', 'tms:create', 'tms:update', 'tms:delete'])
    },
    hasAccessFleetTaskInstant() {
      return this.hasAccessAll([
        'fleet-task-instant:read',
        'fleet-task-instant:create',
        'fleet-task-instant:update',
        'fleet-task-instant:delete',
      ])
    },
    computedStatus() {
      return (item) => {
        const mappedStatus = this.statusIdToLabelMap[item.status?.raw_type]

        if (mappedStatus) {
          return this.$t(mappedStatus)
        }

        return item.status?.name
      }
    },
    hasFeatureFleetTaskContractMGM() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data?.features?.fleet_task_contract
      )
    },
    enableSelectAll() {
      return this.activeFilters['status.name']?.length > 0 && this.hasBulkTransitionFleetTaskFlag
    },
    batchSizeBulkTransition() {
      const flag = this.getFeatureFlagValue('bulkTransitionFleetTaskFlagV2')
      return flag?.batch_size ?? 100
    },
    defaultTooltipText() {
      return this.$t('fleetTask.tooltip.notStarted')
    },
    selectedFleetTaskCount() {
      return Object.keys(this.selectedFleetTaskIds).filter((id) => this.selectedFleetTaskIds[id])
        .length
    },
    mobileActionMenuItems() {
      return this.actionMenuItems(this.showActiveTableDeleteModal)
    },
    isTmsMarketing() {
      return isTmsMarketingUser()
    },
  },
  watch: {
    activeFilters: {
      handler(_) {
        if (Object.keys(this.activeFilters).length > 0) this.hasFilters = true
        else this.hasFilters = false
      },
    },
  },

  created() {
    if (this.$route.query?.type === 'instant') {
      this.selectedTab = 'withoutLoad'
      return
    }

    this.setFilterFromURL()
  },
  mounted() {
    this.checkHeaderViewport()
    this.handleWindowScroll()
    window.addEventListener('resize', this.checkHeaderViewport)
    window.addEventListener('scroll', this.handleWindowScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkHeaderViewport)
    window.removeEventListener('scroll', this.handleWindowScroll)
  },
  methods: {
    formatIndexDate,
    handleWindowScroll() {
      this.hasPageScrolled = window.scrollY > 8
    },
    checkHeaderViewport() {
      this.isMobileHeader = window.innerWidth < this.buttonScreenBreakpoint
    },
    getActiveTableRef() {
      return this.hasAccessFleetTask && this.hasAccessFleetTaskInstant && !this.isTmsMarketing
        ? this.$refs.mcTableWithLoad
        : this.$refs.mcTableWithoutLoad
    },
    showActiveTableDeleteModal() {
      this.getActiveTableRef()?.showDeleteModal?.()
    },
    handleUnselectAllFleetTasks() {
      this.getActiveTableRef()?.handleUnselectAll?.()
    },
    normalizeLineOrderPosition(fields) {
      const numberIndex = fields.findIndex((field) => field.key === 'number')
      const lineOrderIndex = fields.findIndex((field) => field.key === 'line_order')

      if (numberIndex === -1 || lineOrderIndex === -1) {
        return fields
      }

      const normalizedFields = [...fields]
      const [lineOrderField] = normalizedFields.splice(lineOrderIndex, 1)
      const insertIndex = lineOrderIndex > numberIndex ? numberIndex + 1 : numberIndex
      normalizedFields.splice(insertIndex, 0, lineOrderField)

      return normalizedFields
    },
    handleSetFleetTaskColumns(fields, handleSetDataFields) {
      handleSetDataFields(this.normalizeLineOrderPosition(fields))
    },

    handleDataAvailable(isAvailable) {
      this.isTableDataAvailable = isAvailable
    },

    handleClickRow(item) {
      const query = this.$route.query

      this.$router.push({ name: 'Fleet Task Detail', params: { id: item.id }, query })
    },

    handleOpenNewTab(item) {
      const query = this.$route.query

      return this.$router.resolve({ name: 'Fleet Task Detail', params: { id: item.id }, query })
        .href
    },

    handleAdd() {
      const query = this.$route.query
      const name = this.hasAccessFleetTask ? 'Fleet Task Create' : 'Fleet Task Instant Create'

      this.$router.push({ name, query })
    },

    handleDeleteError(errors) {
      let errorMessage = this.$t('global.somethingWentWrong')

      if (errors.status === 400) {
        if (errors.message === "can't delete fleet task that has vendor request delivery order") {
          errorMessage = toastErrors['failed-to-delete-with-vendor']
        } else {
          const allErrors = Object.values(errors.errors || {}).flat()

          if (allErrors.includes('invalid status')) {
            errorMessage = toastErrors['failed-to-bulk-delete']
          } else if (allErrors.includes('fleet task is from vendor request')) {
            errorMessage = toastErrors['is-from-vendor-request']
          }
        }
      }

      this.$mcToast.error(errorMessage)
    },

    handleChangeActiveFilters(filters) {
      /*
        filters is Array of Object
        example:
        [
          {
            key: 'created_on:lte',
            values: '2023-08-29T17:00:00Z'
          }
        ]
        */

      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null or undefined
        if (filter.values === null || filter.values === undefined || filter.values === '') return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })

      this.$refs.mcTableWithLoad?.handleUnselectAll()
      this.$refs.mcTableWithoutLoad?.handleUnselectAll()
    },

    handleSetParams(params) {
      this.params = params
      this.setURLParams(params)
    },

    handleSetMetadata(metadata) {
      this.metadata = metadata
    },

    // change current URL based on params changes
    setURLParams(params) {
      const newParams = {}

      // loop the params to set the newParams
      for (const [key, val] of Object.entries(params)) {
        // skip the undefined value
        if (val == undefined) continue

        // immediately set the value if it's not an Array
        if (!Array.isArray(val)) {
          newParams[key] = typeof val === 'string' ? val : val.toString()

          continue
        }

        // value with Array type usually is filters/sorts, so we need
        // to iterate it and set to each key
        val.forEach((item, index) => {
          newParams[key + index] = item
        })
      }

      // check if the URL has same values with new params
      const isSameParams = isEqual(newParams, this.$route.query)
      if (isSameParams) return

      // redirect to new query params
      const urlPath = '/fleet-task?' + new URLSearchParams(newParams).toString()
      this.$router.replace(urlPath)
    },

    // change filters from current URL
    setFilterFromURL() {
      const activeFilters = new Map()
      for (const [key, val] of Object.entries(this.$route.query)) {
        // set initial params exclude filters (page, limit, search)
        if (!key.includes('filters')) {
          let value
          if (key === 'search') {
            value = val || '-'
          } else if (key === 'limit') {
            value = [10, 25, 50, 100].includes(val) ? Number(val) || 1 : 10
          } else if (key === 'page') {
            value = Number(val) || 1
          }

          this.$set(this.initialParams, key, value)
          continue
        }

        const field = val.split(' ')[0]

        // TODO: Set filters from table filter
        if (field !== 'status.name') continue

        const { op, value } = formatFilterMappings[field](val)
        let newKey = `${field}:${op}`

        if (field === 'status.name') {
          newKey = field
        }

        // set new key in active filters
        activeFilters.set(newKey, value)
      }

      // if there are active filters, set the new values
      if (activeFilters.size == 0) return

      this.activeFilters = Object.fromEntries(activeFilters)
    },

    handleImport() {
      const type = this.$route.query?.type
      this.$router.push({
        name: `${type === 'instant' ? 'Fleet Task Instant' : 'Fleet Task'} Import`,
      })
    },

    handleDownload(format, dataAmount) {
      // Remove undefined params
      const params = omitBy(
        {
          ...this.params,
          format,
        },
        (value) => value === undefined
      )

      if (dataAmount === 'all') {
        delete params['limit']
        delete params['page']
      }

      window.open(
        `${process.env.VUE_APP_API_FLEET_PLANNING_URL}/fleet-task?${new URLSearchParams(
          params
        ).toString()}`,
        '_self'
      )
    },

    isDisabled(item) {
      if (item?.track_link === '') {
        return true
      }
      const disabledStatuses = new Set(['DRAFT', 'SCHEDULED', 'CANCELED'])
      return disabledStatuses.has(item.status.raw_type)
    },

    trackFO(data) {
      this.dataFO = data || {}
      this.$bvModal.show('modal-link')
    },

    changeTab(value) {
      this.selectedTab = value
    },
    getSelectedItemsCountText(count) {
      return this.$t('fleetTask.bulkTransition.itemsLabel', { count })
    },
    getBulkTransitionActionText(statusId) {
      const actionMap = {
        WAITING_FOR_APPROVAL: 'fleetTask.bulkTransition.action.waitingForApprovalPast',
        SCHEDULED: 'fleetTask.bulkTransition.action.scheduledPast',
        STARTED: 'fleetTask.bulkTransition.action.startedPast',
        ENDED: 'fleetTask.bulkTransition.action.endedPast',
        CANCELED: 'fleetTask.bulkTransition.action.canceledPast',
      }

      return actionMap[statusId] ? this.$t(actionMap[statusId]) : statusId || ''
    },
    getStrongStatusLabel(labelKey) {
      return `<strong>${this.$t(labelKey)}</strong>`
    },
    showTooltip(data) {
      return data?.vendor?.id ? this.$t('fleetTask.tooltip.notScheduled') : this.defaultTooltipText
    },
    handleSelectValue(selectedIds, selectedItems) {
      this.selectedFleetTaskIds = selectedIds || {}

      if (selectedItems) {
        this.selectedFleetTasks = selectedItems
        return
      }

      const selectedIdList = Object.keys(this.selectedFleetTaskIds).filter(
        (id) => this.selectedFleetTaskIds[id]
      )
      const nextSelectedFleetTasks = Object.fromEntries(
        selectedIdList.map((id) => [id, this.selectedFleetTasks?.[id]]).filter(([, item]) => !!item)
      )

      const itemsFromTableWithLoad = this.$refs.mcTableWithLoad?.items ?? []
      const itemsFromTableWithoutLoad = this.$refs.mcTableWithoutLoad?.items ?? []

      for (const item of [...itemsFromTableWithLoad, ...itemsFromTableWithoutLoad]) {
        if (!item?.id) continue
        if (this.selectedFleetTaskIds[item.id]) {
          nextSelectedFleetTasks[item.id] = item
        }
      }

      this.selectedFleetTasks = nextSelectedFleetTasks
    },
    getSelectedItems() {
      return Object.keys(this.selectedFleetTaskIds).filter((id) => this.selectedFleetTaskIds[id])
    },
    getCurrentStatusFromFilter() {
      const statusFromFilter = this.activeFilters['status.name']?.[0]
      if (statusFromFilter) {
        return statusFromFilter
      }

      const selectedIds = this.getSelectedItems()
      const uniqueStatus = new Set()

      for (const id of selectedIds) {
        const rawType = this.selectedFleetTasks?.[id]?.status?.raw_type
        uniqueStatus.add(rawType || '__UNKNOWN__')
      }

      if (uniqueStatus.size === 0) return null
      if (uniqueStatus.size === 1) return [...uniqueStatus][0]
      return '__MULTIPLE__'
    },
    async handleBulkTransition(newStatus, additionalPayload = {}) {
      if (this.isTransitionLoading) {
        return
      }

      const targetStatus = status[newStatus]
      const BATCH_SIZE = this.batchSizeBulkTransition
      try {
        const selectedItems = this.getSelectedItems()
        if (selectedItems.length === 0) {
          this.$mcToast.error(this.$t('fleetTask.bulkTransition.selectDataFirst'))
          return
        }

        this.isTransitionLoading = true

        const allIds = [...selectedItems]
        const firstBatchIds = allIds.slice(0, BATCH_SIZE)
        const remainingIds = allIds.slice(BATCH_SIZE)

        const data = {
          ids: firstBatchIds,
          status: newStatus,
          ...additionalPayload,
        }

        const res = await Transition.bulkTransition(data)
        const transitionActionText = this.getBulkTransitionActionText(targetStatus?.id)

        this.$nextTick(() => this.$refs.transitionToCancelModal.hide())

        if (res.errors) {
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: this.$t('fleetTask.bulkTransition.failedMessage', {
                    count: firstBatchIds.length,
                    status: transitionActionText,
                  }),
                },
              ],
            },
          })
          return
        }

        const succeededCount = res?.data?.total_succeeded_count || 0
        const failedCount = res?.data?.total_failed_count || 0
        const failedItems = res?.data?.failed || []

        if (failedCount > 0) {
          if (targetStatus?.id === 'CANCELED') {
            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    html: this.$t('fleetTask.bulkTransition.failedMessage', {
                      count: failedCount,
                      status: transitionActionText,
                    }),
                  },
                ],
              },
            })
          } else {
            this.$mcToast.error(this.$t('fleetTask.bulkTransition.failedToChangeStatus'))
          }
        }

        if (succeededCount > 0) {
          this.$mcToast.success({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: this.$t('fleetTask.bulkTransition.successMessage', {
                    count: succeededCount,
                    status: transitionActionText,
                  }),
                },
              ],
            },
          })
        }

        const idsToAutoSelect = [...remainingIds]

        if (this.$refs.mcTableWithLoad) {
          await this.$refs.mcTableWithLoad.getData({
            limit: this.params.limit,
            page: this.params.page,
            search: this.initialParams.search,
          })
        }
        if (this.$refs.mcTableWithoutLoad) {
          await this.$refs.mcTableWithoutLoad.getData({
            limit: this.params.limit,
            page: this.params.page,
            search: this.initialParams.search,
          })
        }

        this.hasLimitBulkTransition = idsToAutoSelect.length > BATCH_SIZE

        await this.$nextTick()

        if (idsToAutoSelect.length > 0) {
          this.$refs.mcTableWithLoad?.handleSelectSpecificIds?.(idsToAutoSelect)
          this.$refs.mcTableWithoutLoad?.handleSelectSpecificIds?.(idsToAutoSelect)
          if (succeededCount > 0) {
            this.$mcToast.success({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    html: this.$t('fleetTask.bulkTransition.autoSelectedNext', {
                      count: idsToAutoSelect.length,
                    }),
                  },
                ],
              },
            })
          }
        } else {
          this.$refs.mcTableWithLoad?.handleUnselectAll?.()
          this.$refs.mcTableWithoutLoad?.handleUnselectAll?.()
        }
      } catch (error) {
        console.error('Error transitioning fleet tasks:', error)
        this.$mcToast.error(this.$t('fleetTask.bulkTransition.errorWhileChangingStatus'))
      } finally {
        this.isTransitionLoading = false
      }
    },
    openBulkTransitionModal(nextStatus) {
      const selectedItems = this.getSelectedItems()
      if (selectedItems.length === 0) {
        this.$mcToast.error(this.$t('fleetTask.bulkTransition.selectDataFirst'))
        return
      }

      if (selectedItems.length > this.batchSizeBulkTransition) {
        this.hasLimitBulkTransition = true
      }

      this.selectedItemsForTransition = selectedItems
      this.newStatusTransition = {
        status: nextStatus,
      }

      this.$nextTick(() => {
        this.$refs.bulkTransitionModal.show()
      })
    },
    validateBulkStatus(currentStatus, allowedStatuses, invalidLabel, oldStatusLabel = null) {
      const invalidLabelHtml = invalidLabel.includes('<')
        ? invalidLabel
        : `<strong>${invalidLabel}</strong>`

      if (currentStatus === '__MULTIPLE__' || currentStatus === '__UNKNOWN__') {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('fleetTask.bulkTransition.invalidStatus', {
                  status: invalidLabelHtml,
                }),
              },
            ],
          },
        })
        return false
      }

      const isValid = allowedStatuses.includes(currentStatus)

      if (!isValid) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('fleetTask.bulkTransition.invalidStatus', {
                  status: invalidLabelHtml,
                }),
              },
            ],
          },
        })
        return false
      }

      if (oldStatusLabel) this.oldStatusFleetTaskLabel = oldStatusLabel
      return true
    },

    handleSendApproval() {
      const currentStatus = this.getCurrentStatusFromFilter()
      if (currentStatus) {
        const ok = this.validateBulkStatus(
          currentStatus,
          ['DRAFT'],
          this.$t('fleetTask.status.draft'),
          status.DRAFT.label
        )
        if (!ok) return
      }
      this.openBulkTransitionModal(status.WAITING_FOR_APPROVAL)
    },

    handleTransitionToScheduled() {
      const currentStatus = this.getCurrentStatusFromFilter()
      if (currentStatus) {
        const allowedStatuses = this.hasFeatureFleetTaskContractMGM
          ? ['DRAFT', 'APPROVED']
          : ['DRAFT']
        const invalidLabel = this.hasFeatureFleetTaskContractMGM
          ? `${this.getStrongStatusLabel('fleetTask.status.draft')} ${this.$t(
              'global.or'
            )} ${this.getStrongStatusLabel('fleetTask.status.approved')}`
          : this.$t('fleetTask.status.draft')
        const previousStatusLabel =
          currentStatus === 'APPROVED' ? 'fleetTask.status.approved' : status.DRAFT.label
        const ok = this.validateBulkStatus(
          currentStatus,
          allowedStatuses,
          invalidLabel,
          previousStatusLabel
        )
        if (!ok) return
      }
      this.openBulkTransitionModal(status.SCHEDULED)
    },

    handleTransitionStarted() {
      const currentStatus = this.getCurrentStatusFromFilter()
      if (currentStatus) {
        const ok = this.validateBulkStatus(
          currentStatus,
          ['SCHEDULED'],
          this.$t('fleetTask.status.scheduled'),
          status.SCHEDULED.label
        )
        if (!ok) return
      }
      this.openBulkTransitionModal(status.STARTED)
    },

    handleTransitionToEnded() {
      const currentStatus = this.getCurrentStatusFromFilter()
      if (currentStatus) {
        const ok = this.validateBulkStatus(
          currentStatus,
          ['STARTED'],
          this.$t('fleetTask.bulkTransition.action.started'),
          status.STARTED.label
        )
        if (!ok) return
      }
      this.openBulkTransitionModal(status.ENDED)
    },

    handleTransitionToCanceled() {
      const selectedItems = this.getSelectedItems()
      this.selectedItemsForTransition = selectedItems
      this.newStatusTransition = { status: status.CANCELED }
      this.$nextTick(() => this.$refs.transitionToCancelModal.show())
    },
    handleBulkTransitionConfirm(data) {
      this.handleBulkTransition(this.newStatusTransition?.status?.id, data.additionalPayload)
      this.$nextTick(() => {
        this.$refs.bulkTransitionModal.hide()
      })
    },
    handleCancelBulkTransition() {
      this.$nextTick(() => {
        this.$refs.bulkTransitionModal.hide()
      })
      this.hasLimitBulkTransition = false
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-fleet-task {
  .mc-list-header {
    margin-bottom: 24px;
  }

  .fleet-task-header-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .fleet-task-header-actions--mobile {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-start;
    flex-wrap: nowrap;
  }

  .fleet-task-table-toolbar__row {
    gap: 12px;
  }

  .fleet-task-table-toolbar__actions {
    flex-wrap: wrap;
  }

  .create-dropdown {
    &-item {
      width: 250px;
    }

    .dropdown-menu {
      margin-top: 4px;
    }
  }

  :deep(.btn) {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @media screen and (max-width: 767px) {
    .mc-list-header {
      margin-bottom: 16px;
    }

    :deep(.banner-header) {
      padding: 0px;
    }

    :deep(.banner-header .row) {
      margin: 0;
    }

    :deep(.banner-header .row > .col-12) {
      padding-left: 0;
      padding-right: 0;
    }

    :deep(.banner-header .row > .col-12.d-flex) {
      align-items: flex-start !important;
    }

    .mc-list-header--sticky {
      position: sticky;
      top: 70px;
      z-index: 116;
      width: auto;
      margin-left: -18px;
      margin-right: -18px;
      box-sizing: border-box;

      :deep(.banner-header) {
        margin: 0;
        padding: 12px 18px 16px;
        background: $white;
        border-bottom: 1px solid #edf2f7;
        box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
      }
    }

    :deep(.mc-table-footer) {
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }

    :deep(.mc-limit) {
      flex-wrap: nowrap;
      justify-content: center;
      gap: 6px;
      font-size: 12px;
    }

    :deep(.mc-limit .__limit),
    :deep(.mc-limit .__info) {
      justify-content: center;
      text-align: center;
      white-space: nowrap;
      width: auto;
      flex: 0 0 auto;
    }

    :deep(.mc-pagination-container) {
      justify-content: center;
    }

    :deep(.before-table > .mc-row-check) {
      display: none;
    }

    .fleet-task-table-toolbar__row {
      flex-wrap: wrap;
      align-items: center;
    }

    .fleet-task-table-toolbar__actions {
      flex: 0 0 auto;
      margin-left: auto;
      justify-content: flex-end;
    }

    :deep(.fleet-task-table-toolbar__inline-filter) {
      flex: 1 1 auto;
      min-width: 0;
      padding-bottom: 2px;
    }

    :deep(.slot-item-end) {
      width: 100%;
      justify-content: flex-start !important;
    }

    :deep(.fleet-task-table-toolbar__view-manager) {
      order: 2;
      flex: 0 0 auto;
    }

    :deep(.fleet-task-table-toolbar__view-manager .mc-button-wrapper) {
      width: auto;
    }

    :deep(.fleet-task-table-toolbar__view-manager .mc-button) {
      width: auto;
      justify-content: center;
      padding-left: 8px;
      padding-right: 8px;
    }

    :deep(.mc-has-search) {
      order: 1;
      flex: 1 1 0;
      min-width: 0;
    }

    .fleet-task-table-toolbar__search-actions {
      order: 3;
      flex: 0 0 auto;
    }

    .fleet-task-table-toolbar__selected-counter {
      order: 4;
      width: 100%;
      margin-bottom: 0;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 4px;
    }

    :deep(.fleet-task-table-toolbar__search-actions .mc-button) {
      padding-left: 8px;
      padding-right: 8px;
    }

    :deep(.fleet-task-header-actions__download),
    :deep(.fleet-task-header-actions__upload) {
      flex: 0 0 auto;
    }

    :deep(.fleet-task-header-actions__download .dropdown-menu) {
      left: 0 !important;
      right: auto !important;
      margin-left: 0px;
      max-width: calc(100vw - 32px);
    }

    :deep(.fleet-task-header-actions__create-button) {
      flex: 1 1 0;
      min-width: 0;
    }

    :deep(.fleet-task-header-actions__create-button .mc-button) {
      width: 100%;
      justify-content: center;
    }

    :deep(.fleet-task-header-actions__create-dropdown) {
      display: flex;
      flex: 1 1 0;
      min-width: 0;
    }

    :deep(.fleet-task-header-actions__create-dropdown .mc-button-wrapper) {
      width: 100%;
    }

    :deep(.fleet-task-header-actions__create-dropdown .mc-button) {
      width: 100%;
      justify-content: center;
    }

    :deep(.fleet-task-header-actions__create-dropdown .mc-button .mc-icon) {
      margin-left: auto;
    }

    :deep(.fleet-task-table-toolbar__search-actions #actions-dropdown .mc-button) {
      padding-left: 8px;
      padding-right: 8px;
      min-width: 40px;
      justify-content: center;
    }
  }
}
</style>

<style lang="scss">
.mc-fleet-task {
  padding-bottom: 25px;

  .page-header {
    .banner-header {
      margin: 0;
    }
  }

  .__status-dots {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }
}

.mc-fleet-task .mc-table .card {
  border: 1px solid #e6e7ec;
  padding: 1em;
  background-color: #fff;
}
</style>